import { StackLayout } from '@euler/app/components/StackLayout';
import { WeworkIcon } from '@euler/app/components/icons/WeworkIcon';
import { Typography } from '@euler/app/components/typography';
import { useIsWeixinOpenAuthorized } from '@euler/app/flows/auth/context';
import { QrcodeSharingView } from '@euler/app/flows/common/components/QrcodeSharingView';
import { QrcodeSharing } from '@euler/app/flows/common/components/QrcodeSharingView.shared';
import { Center } from '@euler/components/Center';
import { Colors, StatusColors } from '@euler/components/Colors';
import { Icon } from '@euler/components/Icomoon';
import Euler from '@euler/components/layout';
import { useIsHub } from '@euler/functions/isHub';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useIsMobileLayout } from '@euler/functions/responsiveness';
import { useShareReport } from '@euler/functions/sharing/useShareReport';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { MediaService } from '@euler/lib/media/MediaService';
import { useServiceFactory } from '@euler/services/factory';
import { onErrorIgnore } from '@euler/utils';
import { usePersistFn } from '@euler/utils/hooks';
import { useNavigation } from '@react-navigation/native';
import { CardStyleInterpolators } from '@react-navigation/stack';
import * as FileSystem from 'expo-file-system';
import { MotiView } from 'moti';
import {
  ReactNode,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  Platform,
  ScrollView,
  StyleProp,
  StyleSheet,
  TouchableWithoutFeedback,
  View,
  ViewStyle,
  useWindowDimensions,
} from 'react-native';
import Toast from 'react-native-root-toast';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import * as WeChat from 'react-native-wechat-lib';

const kSpacing = 30;

export type QrcodeSharingCloseOrigin = 'more';

export const QrcodeSharingScreen = wrapNavigatorScreen(
  ({
    orderNo,
    taskNo,
    reportNo,
    licensePlateNo,
    storeName,
    top,
    onClose,
  }: {
    orderNo: string;
    taskNo: string;
    reportNo: string;
    licensePlateNo: string;
    storeName: string;
    top?: number;
    onClose?: (origin?: QrcodeSharingCloseOrigin) => void;
  }) => {
    const isHub = useIsHub();
    const isMobileLayout = useIsMobileLayout();
    const dimens = useWindowDimensions();
    const safeAreaInsets = useSafeAreaInsets();
    const navigation = useNavigation();
    const { rpx } = useResponsiveContext();
    const { taskService } = useServiceFactory();
    const isWeixinOpenAuthorized = useIsWeixinOpenAuthorized();

    const [isQrcodeLoaded, setIsQrcodeLoaded] = useState(false);

    const qrcodeSharing = useRef<QrcodeSharing.QrcodeSharingAPI>(null);

    const spacing = rpx(kSpacing);
    const aspectRatio = 375 / 500;
    const maxWidth = dimens.width;
    const maxHeight = dimens.height * 0.7;

    let width = 0;

    if (isMobileLayout || dimens.width <= rpx(375)) {
      width = dimens.width;
    } else {
      if (dimens.width > dimens.height) {
        // landscape
        width = maxHeight * aspectRatio;
        if (width >= maxWidth) {
          width = maxWidth;
        }
      } else {
        // portrait
        width = maxWidth;
        const height = width / aspectRatio;
        if (height >= maxHeight) {
          width = maxHeight * aspectRatio;
        }
      }
    }

    const cardWidth = width - 2 * spacing;
    const cardHeight = Math.round((cardWidth * 500) / 375);

    const [model, setModel] = useState<QrcodeSharing.Model>();

    useEffect(() => {
      taskService
        .getSubscribeBindQrcodeImageUrl(taskNo, {
          logo: false,
          size: cardWidth,
        })
        .then(qrcodeUrl => {
          setModel({
            title: '长按识别二维码绑定车辆',
            subTitle: '获取车辆检测/施工报告',
            licensePlateNo,
            storeName,
            qrcodeUrl,
          });
        })
        .catch(onErrorIgnore);
    }, [cardWidth, licensePlateNo, storeName, taskNo, taskService]);

    const widthStyle =
      width === dimens.width
        ? undefined
        : {
            width,
          };

    const closed = useRef(false);
    const close = useCallback(
      (action?: () => any, origin?: QrcodeSharingCloseOrigin) => {
        if (closed.current) return;
        closed.current = true;
        navigation.goBack();
        setTimeout(() => {
          try {
            void action?.();
          } catch (e) {
            console.error(e);
          }
          onClose?.(origin);
        }, 350);
      },
      [navigation, onClose],
    );

    const onSaveToPhotoAlbum = useCallback(async () => {
      try {
        if (!qrcodeSharing.current) return;

        const localFileUri = await qrcodeSharing.current.save();
        if (!localFileUri) {
          Toast.show('生成图片失败', {
            duration: 500,
            position: Toast.positions.CENTER,
          });
          return;
        }

        await MediaService.shared.saveToLibrary({
          type: 'localFileUri',
          fileUri: localFileUri,
        });

        close(() => {
          Toast.show('已保存图片到本地', {
            duration: 1500,
            position: Toast.positions.BOTTOM,
          });
        });
      } catch (e) {
        console.error(e);
        Toast.show('保存失败', {
          duration: 500,
          position: Toast.positions.CENTER,
        });
      }
    }, [close]);

    const onShareQrcode = useCallback(async () => {
      if (!qrcodeSharing.current) return;

      if (Platform.OS === 'web') {
        Toast.show('暂不支持浏览器分享', {
          duration: 500,
          position: Toast.positions.BOTTOM,
        });
        return;
      }

      if (!(await WeChat.isWXAppInstalled())) {
        Toast.show('未安装微信', {
          duration: 500,
          position: Toast.positions.BOTTOM,
        });
        return;
      }

      try {
        const localFileUri = await qrcodeSharing.current.save();
        if (!localFileUri) {
          throw new Error('failed to save to album');
        }

        await WeChat.shareLocalImage({
          imageUrl: localFileUri.replace('file://', ''),
          scene: 0,
        });

        close(async () => {
          Toast.show('分享成功', {
            duration: 1000,
            position: Toast.positions.BOTTOM,
          });

          await FileSystem.deleteAsync(localFileUri).catch(onErrorIgnore);
        });
      } catch (e) {
        console.error(e);
        Toast.show('分享失败', {
          duration: 1500,
          position: Toast.positions.BOTTOM,
        });
      }
    }, [close]);

    const {
      isWeixinSharingAvailable,
      isWeWorkSharingAvailable,
      shareReportViaWeixin,
      shareReportViaWeWork,
    } = useShareReport();

    const onShareReportViaWeixin = usePersistFn(() => {
      void shareReportViaWeixin({
        target: { type: 'task', id: taskNo },
        licensePlateNo,
        orderNo,
        reportNo,
        success: () => {
          close();
        },
      });
    });

    const onShareReportViaWeWork = usePersistFn(() => {
      void shareReportViaWeWork({
        target: { type: 'task', id: taskNo },
        licensePlateNo,
        orderNo,
        reportNo,
        success: () => {
          close();
        },
      });
    });

    const onCancel = useCallback(() => {
      close();
    }, [close]);

    const onQrcodeLoaded = useCallback(() => {
      setIsQrcodeLoaded(true);
    }, []);

    const onMorePress = useCallback(() => {
      close(undefined, 'more');
    }, [close]);

    return (
      <View
        css={`
          flex: 1;
          background-color: rgba(0, 0, 0, 0.9);
        `}
        style={widthStyle ? { alignItems: 'center' } : undefined}
      >
        <TouchableWithoutFeedback
          onPress={() => {
            close();
          }}
        >
          <View style={StyleSheet.absoluteFill} />
        </TouchableWithoutFeedback>

        <ScrollView
          style={widthStyle}
          contentContainerStyle={{
            flexGrow: 1,
            paddingTop: (top ?? safeAreaInsets.top + rpx(44)) + rpx(15),
            alignItems: 'center',
          }}
        >
          <MotiView
            from={{ translateY: rpx(100) }}
            animate={{ translateY: 0 }}
            css={`
              width: ${cardWidth}px;
              height: ${cardHeight}px;
              background-color: #fff;
              border-radius: ${rpx(10)}px;
              overflow: hidden;
            `}
          >
            {model ? (
              <QrcodeSharingView
                ref={qrcodeSharing}
                model={model}
                width={cardWidth}
                height={cardHeight}
                type="image/jpeg"
                quality={0.9}
                isWeixinOpenAuthorized={isWeixinOpenAuthorized}
                onQrcodeLoaded={onQrcodeLoaded}
              />
            ) : null}
          </MotiView>
        </ScrollView>
        {isHub ? null : (
          <ActionBar
            isQrcodeAvailable={isWeixinOpenAuthorized}
            isQrcodeLoaded={isQrcodeLoaded}
            isWeixinSharingAvailable={isWeixinSharingAvailable}
            isWeWorkSharingAvailable={isWeWorkSharingAvailable}
            onShareReportViaWeixin={onShareReportViaWeixin}
            onShareReportViaWeWork={onShareReportViaWeWork}
            onShareQrcode={onShareQrcode}
            onSaveToPhotoAlbum={onSaveToPhotoAlbum}
            onCancel={onCancel}
            onMorePress={onMorePress}
          />
        )}
      </View>
    );
  },
  {
    presentation: 'transparentModal',
    animationEnabled: true,
    headerShown: false,
    cardOverlayEnabled: true,
    cardStyle: {
      backgroundColor: 'transparent',
    },
    cardStyleInterpolator: CardStyleInterpolators.forFadeFromBottomAndroid,
  },
);

const ActionBar = memo(
  ({
    isQrcodeLoaded,
    isQrcodeAvailable,
    isWeixinSharingAvailable,
    isWeWorkSharingAvailable,
    onShareReportViaWeixin,
    onShareReportViaWeWork,
    onShareQrcode,
    onSaveToPhotoAlbum,
    onMorePress,
    onCancel,
  }: {
    isQrcodeLoaded?: boolean;
    isQrcodeAvailable?: boolean;
    isWeixinSharingAvailable?: boolean;
    isWeWorkSharingAvailable?: boolean;
    onShareQrcode?: () => void;
    onSaveToPhotoAlbum?: () => void;
    onShareReportViaWeixin?: () => void;
    onShareReportViaWeWork?: () => void;
    onMorePress?: () => void;
    onCancel?: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const { bottom } = useSafeAreaInsets();

    return (
      <Euler.View backgroundColor="#fff" alignSelf="stretch">
        <StackLayout
          gap={rpx(isWeWorkSharingAvailable ? 15 : 32)}
          direction="row"
          alignItems="center"
          justifyContent="center"
          backgroundColor={Colors.Gray9}
          padding={rpx(16)}
        >
          {isWeixinSharingAvailable && isQrcodeAvailable ? (
            <ActionButton
              text="分享二维码"
              disabled={!isQrcodeLoaded}
              onPress={onShareQrcode}
              icon={
                <Icon
                  name="wechat"
                  size={rpx(32)}
                  color={StatusColors.Success}
                />
              }
            />
          ) : null}
          {isQrcodeAvailable && (
            <ActionButton
              text="保存到相册"
              disabled={!isQrcodeLoaded}
              onPress={onSaveToPhotoAlbum}
              icon={
                <Icon name="download-outline" size={rpx(24)} color="black" />
              }
            />
          )}
          {isWeixinSharingAvailable ? (
            <ActionButton
              text="分享报告"
              onPress={onShareReportViaWeixin}
              icon={
                <Icon
                  name="share-arrow"
                  size={rpx(32)}
                  color={StatusColors.Success}
                />
              }
            />
          ) : null}
          {isWeWorkSharingAvailable ? (
            <ActionButton
              text="分享报告"
              icon={<WeworkIcon size={rpx(32)} />}
              onPress={onShareReportViaWeWork}
            />
          ) : null}
          <ActionButton
            text="更多"
            onPress={onMorePress}
            icon={
              <Icon
                name="more-vertical"
                size={rpx(32)}
                style={{
                  transform: [{ rotate: '90deg' }, { translateX: rpx(3) }],
                }}
              />
            }
          />
        </StackLayout>

        <Euler.View pb={bottom} backgroundColor="#fff">
          <Center.Touchable height={rpx(44)} onPress={onCancel}>
            <Typography.Text size={rpx(15)} color="#000">
              取消
            </Typography.Text>
          </Center.Touchable>
        </Euler.View>
      </Euler.View>
    );
  },
);

const ActionButton = memo(
  ({
    text,
    icon,
    style,
    disabled,
    onPress,
  }: {
    text: string;
    icon: ReactNode;
    disabled?: boolean;
    style?: StyleProp<ViewStyle>;
    onPress?: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    return (
      <Center.Touchable onPress={onPress} disabled={disabled} style={style}>
        <Center
          width={rpx(56)}
          height={rpx(56)}
          borderRadius={rpx(10)}
          backgroundColor="#fff"
        >
          {icon}
        </Center>
        <Typography.Text size={rpx(12)} mt={rpx(8)} color={Colors.Gray1}>
          {text}
        </Typography.Text>
      </Center.Touchable>
    );
  },
);
